import React, { useState, useEffect } from "react";
import styles from "./appointments-history.module.css";
import PageTitle from "../../components/screens/page-title";
import Table from "../../components/ui/table";
import Input from "../../components/ui/input";
import { useNavigate } from "react-router-dom";
import AppointmentDetailModal from "../../components/modals/appointment-detail-modal";
import { getCitasHistorial } from "../../services/apiService";
import { showErrorToast } from "../../utils/notifications";
import usePageTitle from "../../hooks/usePageTitle";

const AppointmentsHistoryPage = () => {
  usePageTitle('Historial');
  const navigate = useNavigate();
  const [searchValue, setSearchValue] = useState("");
  
  // State for appointments data
  const [historyAppointments, setHistoryAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const pageSize = 50;
  
  // State for modal
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    loadAppointments(currentPage);
  }, [currentPage]);

  const loadAppointments = async (page = 0) => {
    setLoading(true);
    try {
      const history = await getCitasHistorial(page, pageSize);
      // Manejar respuesta paginada si viene en ese formato
      const historyData = history?.content && Array.isArray(history.content) 
        ? history.content 
        : (Array.isArray(history) ? history : []);
      setHistoryAppointments(historyData);
      
      // Actualizar información de paginación
      if (history?.totalPages !== undefined) {
        setTotalPages(history.totalPages);
        setTotalElements(history.totalElements || 0);
      }
    } catch (error) {
      console.error("Error loading appointments:", error);
      showErrorToast("Error al cargar las citas");
    } finally {
      setLoading(false);
    }
  };

  const headers = [
    { label: "Expediente", key: "expedienteId" },
    { label: "Dirección", key: "domicilioCliente" },
    { label: "Localidad", key: "localidadCliente" },
    { label: "Fecha Inicio", key: "fechaCita" },
    { label: "Fecha Fin", key: "fechaCitaFin" },
  ];

  const handleSearch = (e) => {
    setSearchValue(e.target.value);
  };

  const filteredAppointments = historyAppointments.filter(app => 
    app.expedienteId.toString().includes(searchValue.toLowerCase()) ||
    app.domicilioCliente.toLowerCase().includes(searchValue.toLowerCase()) ||
    app.localidadCliente.toLowerCase().includes(searchValue.toLowerCase())
  );

  const handleViewDetails = (appointment, e) => {
    console.log("Viewing details for appointment:", appointment);
    
    // Verificar si es click de rueda del ratón o modificador (Ctrl/Cmd) para abrir en nueva pestaña
    try {
      const isMiddle = !!(e && (e.nativeEvent?.button === 1 || e.button === 1 || e.which === 2));
      const isModifier = !!(e && (e.ctrlKey || e.metaKey));
      
      if ((isMiddle || isModifier) && appointment.expedienteId) {
        window.open(`/record/${appointment.expedienteId}`, '_blank');
        return;
      }
    } catch (err) {
      console.error("Error handling click event:", err);
    }
    
    // Abrir modal normalmente si no es middle-click
    setSelectedAppointment(appointment);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedAppointment(null);
  };

  const handleModalUpdate = () => {
    loadAppointments();
  };

  const formatDate = (dateString) => {
    if (!dateString) return "No definida";
    const date = new Date(dateString);
    
    // Detectar si es una cita "sin hora" (00:00 o 01:00)
    const hours = date.getHours();
    const minutes = date.getMinutes();
    if ((hours === 0 || hours === 1) && minutes === 0) {
      return date.toLocaleDateString("es-ES", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      }) + " - Sin hora";
    }
    
    return date.toLocaleDateString("es-ES", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formattedAppointments = filteredAppointments.map(app => ({
    ...app,
    fechaCita: formatDate(app.fechaCita),
    fechaCitaFin: formatDate(app.fechaCitaFin),
  }));

  return (
    <div className={styles.container}>
      <PageTitle iconName="file" title="Historial de Citas" />
      
      <div className={styles.content}>
        <div className={styles.searchContainer}>
          <Input
            placeholder="Buscar por expediente, dirección o localidad..."
            value={searchValue}
            onChange={handleSearch}
            style={{ width: "100%", maxWidth: "500px" }}
          />
        </div>

        {loading ? (
          <div className={styles.loadingContainer}>
            <p>Cargando historial...</p>
          </div>
        ) : (
          <>
            <div className={styles.paginationInfo}>
              Mostrando {historyAppointments.length} de {totalElements} citas (Página {currentPage + 1} de {totalPages || 1})
            </div>
            <Table
              headers={headers}
              data={formattedAppointments}
              onPress={handleViewDetails}
            />
            {totalPages > 1 && (
              <div className={styles.paginationControls}>
                <button
                  onClick={() => setCurrentPage(0)}
                  disabled={currentPage === 0}
                  className={styles.paginationButton}
                >
                  &#171; Primera
                </button>
                <button
                  onClick={() => setCurrentPage(prev => Math.max(0, prev - 1))}
                  disabled={currentPage === 0}
                  className={styles.paginationButton}
                >
                  &#8249; Anterior
                </button>
                <span className={styles.pageInfo}>
                  Página {currentPage + 1} de {totalPages}
                </span>
                <button
                  onClick={() => setCurrentPage(prev => Math.min(totalPages - 1, prev + 1))}
                  disabled={currentPage >= totalPages - 1}
                  className={styles.paginationButton}
                >
                  Siguiente &#8250;
                </button>
                <button
                  onClick={() => setCurrentPage(totalPages - 1)}
                  disabled={currentPage >= totalPages - 1}
                  className={styles.paginationButton}
                >
                  Última &#187;
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {isModalOpen && selectedAppointment && (
        <AppointmentDetailModal
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          citaId={selectedAppointment.citaId || selectedAppointment.id || selectedAppointment.appointmentId}
          onUpdate={handleModalUpdate}
          isHistory={true}
        />
      )}
    </div>
  );
};

export default AppointmentsHistoryPage;
